import { getConfig } from '@config/index'

import { trustedByLogos } from '@custom/shared/const'

import { type PlansPageData } from './types'

const config = getConfig()

export const plansPageData: PlansPageData = {
  introSection: {
    title: 'Your Superhuman suite',
    items: {
      mail: {
        name: 'Mail',
        copy: 'The most productive email app ever made',
      },
      grammarly: {
        name: 'Grammarly',
        copy: 'Everyone’s favorite AI writing partner',
      },
      coda: {
        name: 'Coda',
        copy: 'The all-in-one AI workspace for teams',
      },
      go: {
        name: 'Go',
        copy: 'AI that works in every app you use',
      },
    },
  },
  plansComparisonSection: {
    scrollToFeatures: 'See all features and compare plans',
    plans: {
      free: {
        kind: 'free',
        title: 'Free',
        targetAudience: 'For individuals',
        description: 'Get started with AI assistance in all your favorite apps.',
        billing: {
          price: '$0 USD',
        },
        cta: {
          disabledLabel: 'Coming soon',
          label: 'Get Superhuman Free',
          floatingLabel: 'Get Free',
          url: `${config.paths.nomos.basePath}${config.paths.nomos.atc}`,
        },
        features: {
          included: 'Free includes:',
          products: {
            mail: 'unavailable',
            grammarly: 'available',
            coda: 'available',
            go: 'available',
          },
          list: [
            'Chat with AI that works in every application and tab',
            'Write without mistakes in 6 languages',
            'Write with AI that adapts to your voice and tone',
            'Create collaborative docs with tables, charts, Kanban boards, and forms',
            'Speed up workflows in docs with formulas and automations',
          ],
        },
      },
      pro: {
        kind: 'pro',
        title: 'Pro',
        targetAudience: 'For professionals and small teams',
        description: 'Stay ahead with proactive AI that works everywhere you do.',
        billing: {
          price: '$12 USD',
          cadence: 'per member / month, billed annually',
          monthlyPrice: {
            price: '$30',
            label: 'when billed monthly',
          },
        },
        cta: {
          disabledLabel: 'Coming soon',
          label: 'Get Superhuman Pro',
          floatingLabel: 'Get Pro',
          url: `${config.sessionTransfer.toGrammarly}?rp_host=${config.domains.grammarly}&rp_path=${config.paths.upgradePro}`,
        },
        features: {
          products: {
            mail: 'unavailable',
            grammarly: 'available',
            coda: 'available',
            go: 'available',
          },
          included: 'Everything included in Free, plus:',
          list: [
            'Get unlimited paragraph rewrites and translations in 19 languages',
            'Spot AI-generated text and fix accidental plagiarism',
            'Ensure your writing is always on brand',
            'Publish docs to custom domains with your team’s branding',
            'Get unlimited doc size with 30-day version history',
            'Create better docs faster with AI templates and insights',
          ],
        },
      },
      business: {
        kind: 'business',
        title: 'Business',
        targetAudience: 'For growing businesses',
        description: 'Grow faster with AI that understands your business.',
        billing: {
          price: '$33 USD',
          cadence: 'per member / month, billed annually',
          monthlyPrice: {
            price: '$40',
            label: 'when billed monthly',
          },
        },
        cta: {
          disabledLabel: 'Coming soon',
          label: 'Get Superhuman Business',
          floatingLabel: 'Get Business',
          url: `${config.sessionTransfer.toGrammarly}?rp_host=${config.domains.grammarly}&rp_path=${config.paths.upgradeBusiness}`,
        },
        features: {
          products: {
            mail: 'available',
            grammarly: 'available',
            coda: 'available',
            go: 'available',
          },
          included: 'Everything included in Pro, plus:',
          list: [
            'Fly through your inbox and save 4 hours every single week',
            'Organize your inbox with AI and respond faster to what matters most',
            'Write emails with personalized AI that sounds like you',
            'View and update HubSpot, Salesforce, and Pipedrive right from your inbox',
            'Get unlimited automations and version history in all your docs',
            'Sync data from Jira, GitHub, Figma, and more into your docs',
          ],
        },
      },
      enterprise: {
        kind: 'enterprise',
        title: 'Enterprise',
        targetAudience: 'For organizations',
        description: 'Scale enterprise-grade AI with advanced security and controls.',
        billing: {
          price: 'Custom',
        },
        cta: {
          disabledLabel: 'Coming soon',
          label: 'Request early access',
          floatingLabel: 'Request early access',
          url: `https://${config.domains.grammarly}${config.paths.earlyAccess}`,
        },
        features: {
          products: {
            mail: 'available',
            grammarly: 'available',
            coda: 'available',
            go: 'available',
          },
          included: 'Everything included in Business, plus:',
          list: [
            'Analytics and reporting',
            'Advanced security and admin controls',
            'SAML single sign-on',
            'Custom roles and permissions',
            'Data loss prevention',
            'BYOK encryption',
            'Dedicated support team',
          ],
        },
      },
    },
  },
  trustedBySection: {
    heading: 'Trusted by the most innovative companies in the world',
    logos: trustedByLogos,
  },
  testimonialsSection: {
    title: 'Trusted by over 40 million people and 50,000 teams',
    testimonials: {
      gonimbly: {
        quote:
          '“I trust how Superhuman designs the overall experience. It’s the speed, thoughtfulness, and quality that for me, make it a no-brainer for any high-performing team.”',
        name: 'Jen Igartua',
        position: 'CEO, Go Nimbly',
      },
      zapier: {
        quote:
          '“Working with Grammarly and Coda is more than the technology, It’s about feeling like you have a trusted partner. That’s still something that’s pretty distinctive in the market.”',
        name: 'Brandon Sammut',
        position: 'Chief People Officer, Zapier',
      },
      dispel: {
        quote:
          '“Superhuman and Grammarly accelerate my communication while giving me confidence and control of my voice. Together, they let me streamline workflows and optimize with automation, AI, and smart design.”',
        name: 'Dean Macris',
        position: 'Chief Information Security Officer, Dispel',
      },
    },
  },
  featuresComparisonSection: {
    title: 'Plans and features',
    products: {
      go: [
        {
          productName: 'Go (Beta)',
          sections: [
            {
              name: 'Features',
              features: [
                {
                  name: 'AI chat',
                  tooltip: 'Collaborate with an AI assistant that works in every tab and tool.',
                  availability: {
                    free: 'Unlimited while in beta',
                    pro: 'Unlimited',
                    business: 'Unlimited',
                    enterprise: ['Early access opens', 'February 2026'],
                  },
                },
                {
                  name: 'Inline suggestions',
                  tooltip:
                    'Get proactive feedback, relevant context, and quick actions, suggested everywhere you’re working.',
                  availability: {
                    free: '✓',
                    pro: '✓',
                    business: '✓',
                    enterprise: ['Early access opens', 'February 2026'],
                  },
                },
                {
                  name: 'Connector and partner agents',
                  tooltip:
                    'Harness the power of AI collaborators that provide specialized support, connecting to all your tools, searching synced data, and taking actions for you.',
                  availability: {
                    free: 'Unlimited access to all new agents while in beta',
                    pro: 'Unlimited access to all new agents while in beta',
                    business: 'Unlimited access to all new agents while in beta',
                    enterprise: ['Early access opens', 'February 2026'],
                  },
                },
              ],
            },
          ],
        },
      ],
      grammarly: [
        {
          productName: 'Grammarly',
          sections: [
            {
              name: 'Writing features',
              features: [
                {
                  name: 'Correctness suggestions',
                  tooltip:
                    'Clean up your writing with automatic grammar, spelling, and punctuation suggestions.',
                  availability: {
                    free: '✓',
                    pro: '✓',
                    business: '✓',
                    enterprise: '✓',
                  },
                },
                {
                  name: 'Tone detection',
                  tooltip:
                    'Know how your text sounds to your audience, so you can confidently send the right message.',
                  availability: {
                    free: '✓',
                    pro: '✓',
                    business: '✓',
                    enterprise: '✓',
                  },
                },
                {
                  name: 'Tone suggestions',
                  tooltip:
                    'Rephrase with AI suggestions that adjust your tone to match your intent.',
                  availability: {
                    free: 'Limited',
                    pro: '✓',
                    business: '✓',
                    enterprise: '✓',
                  },
                },
                {
                  name: 'Sentence rewrites',
                  tooltip:
                    'Quickly rewrite sentences to clarify ideas and better convey your points.',
                  availability: {
                    free: '',
                    pro: '✓',
                    business: '✓',
                    enterprise: '✓',
                  },
                },
                {
                  name: 'Fluency suggestions',
                  tooltip: 'Ensure your words and phrasing sound clear, natural, and fluent.',
                  availability: {
                    free: '',
                    pro: '✓',
                    business: '✓',
                    enterprise: '✓',
                  },
                },
                {
                  name: 'Inclusivity suggestions',
                  tooltip: 'Remove any bias to ensure your language is respectful and empathetic.',
                  availability: {
                    free: '',
                    pro: '✓',
                    business: '✓',
                    enterprise: '✓',
                  },
                },
                {
                  name: 'Citation-style formatting',
                  tooltip:
                    'Fix citation formatting issues in seconds, whether you use APA, MLA, or Chicago style.',
                  availability: {
                    free: '',
                    pro: '✓',
                    business: '✓',
                    enterprise: '✓',
                  },
                },
                {
                  name: 'On-demand AI composition',
                  tooltip:
                    'Instantly generate polished drafts, ideas, document summaries, and email replies with AI prompts.',
                  availability: {
                    free: 'Limited prompts',
                    pro: 'More prompts',
                    business: 'Most prompts',
                    enterprise: 'Most prompts',
                  },
                },
                {
                  name: 'Paragraph translation in 19 languages',
                  tooltip:
                    'Translate your text instantly into Chinese (Simplified), Dutch, English (American), English (British), French, German, Indonesian, Italian, Japanese, Korean, Polish, Portuguese, Portuguese (Brazil), Spanish, Spanish (Argentina), Spanish (Mexico), Swedish, Turkish, and Ukrainian.',
                  availability: {
                    free: 'Limited',
                    pro: 'Unlimited',
                    business: 'Unlimited',
                    enterprise: 'Unlimited',
                  },
                },
                {
                  name: 'Paragraph rewrites in Spanish, French, Portuguese, German, and Italian',
                  tooltip:
                    'Improve your writing in Spanish, French, Portuguese, German, and Italian with one-click rewrites.',
                  availability: {
                    free: 'Limited',
                    pro: 'Unlimited',
                    business: 'Unlimited',
                    enterprise: 'Unlimited',
                  },
                },
                {
                  name: 'Grammarly Proofreader agent',
                  tooltip:
                    'Fix spelling, grammar, tone, and clarity as you write, with real-time proofreading.',
                  availability: {
                    free: 'Limited',
                    pro: 'Unlimited',
                    business: 'Unlimited',
                    enterprise: 'Unlimited',
                  },
                },
                {
                  name: 'AI Detection agent',
                  tooltip:
                    'Protect your work and confirm it’s authentically yours with built-in AI detection.',
                  availability: {
                    free: 'Limited',
                    pro: 'Unlimited',
                    business: 'Unlimited',
                    enterprise: 'Unlimited',
                  },
                },
                {
                  name: 'Plagiarism Checker agent',
                  tooltip:
                    'Check your work against millions of online sources to ensure it’s properly cited.',
                  availability: {
                    free: '',
                    pro: '✓',
                    business: '✓',
                    enterprise: '✓',
                  },
                },
                {
                  name: {
                    prefix: '+',
                    label: 'More',
                    url: config.paths.agentStore,
                  },
                  availability: {
                    free: '',
                    pro: '',
                    business: '',
                    enterprise: '',
                  },
                },
                {
                  name: 'Docs with agents',
                  tooltip: 'Write with AI in a dedicated space designed for your best work.',
                  availability: {
                    free: '✓',
                    pro: '✓',
                    business: '✓',
                    enterprise: '✓',
                  },
                },
              ],
            },
            {
              name: 'Team features',
              features: [
                {
                  name: 'ROI Report',
                  tooltip:
                    'See how Grammarly strengthens your business and bottom line with company-specific analytics.',
                  availability: {
                    free: '',
                    pro: '',
                    business: '',
                    enterprise: '✓',
                  },
                },
                {
                  name: 'Effective Communication Score',
                  tooltip:
                    'See a snapshot of your team’s communication with clear, actionable breakdowns.',
                  availability: {
                    free: '',
                    pro: '',
                    business: '',
                    enterprise: '✓',
                  },
                },
                {
                  name: 'Style guide',
                  tooltip:
                    'Get real-time style guide suggestions to keep your team’s writing consistent and on-brand.',
                  availability: {
                    free: '',
                    pro: '1',
                    business: '1',
                    enterprise: 'Unlimited',
                  },
                },
                {
                  name: 'Brand tones',
                  tooltip:
                    'Turn your brand voice into a tone profile that directs your team to use or avoid certain tones.',
                  availability: {
                    free: '',
                    pro: '1',
                    business: '1',
                    enterprise: 'Unlimited',
                  },
                },
                {
                  name: 'Knowledge Share',
                  tooltip:
                    'Keep teams aligned by surfacing internal info like acronyms, project names, terms, and key contacts. Only available for teams of 2 or more.',
                  availability: {
                    free: '',
                    pro: '✓',
                    business: '✓',
                    enterprise: '✓',
                  },
                },
                {
                  name: 'Snippets',
                  tooltip:
                    'Save time by creating reusable phrases, sentences, or paragraphs that your team can insert instantly.',
                  availability: {
                    free: '',
                    pro: '✓',
                    business: '✓',
                    enterprise: '✓',
                  },
                },
                {
                  name: 'Analytics dashboard',
                  tooltip:
                    'Measure and drive your team’s writing progress with actionable insights.',
                  availability: {
                    free: '',
                    pro: 'Team Analytics',
                    business: 'Team Analytics',
                    enterprise: 'Team and Individual Analytics',
                  },
                },
              ],
            },
          ],
        },
      ],
      mail: [
        {
          productName: 'Mail',
          sections: [
            {
              name: 'AI',
              features: [
                {
                  name: 'Ask AI',
                  tooltip:
                    'Find answers fast without hunting through your inboxes for keywords, senders, or subject lines.',
                  availability: {
                    free: '',
                    pro: '',
                    business: '✓',
                    enterprise: '✓',
                  },
                },
                {
                  name: 'Write with AI (voice and tone match)',
                  tooltip:
                    'Write faster with AI that turns your notes and phrases into full drafts tailored to your style, needs, and audience.',
                  availability: {
                    free: '',
                    pro: '',
                    business: '✓',
                    enterprise: '✓',
                  },
                },
                {
                  name: 'Instant Reply',
                  tooltip:
                    'Start each workday with draft replies ready for every email in your inbox. Just review and send.',
                  availability: {
                    free: '',
                    pro: '',
                    business: '✓',
                    enterprise: '✓',
                  },
                },
                {
                  name: 'Instant Event',
                  tooltip:
                    'Create a new event directly from your email, including title, details, attendees, and the best date and time already chosen for you.',
                  availability: {
                    free: '',
                    pro: '',
                    business: '✓',
                    enterprise: '✓',
                  },
                },
                {
                  name: 'Auto Drafts',
                  tooltip:
                    'Let AI draft your follow-up emails in your own voice. Just review and send.',
                  availability: {
                    free: '',
                    pro: '',
                    business: '✓',
                    enterprise: '✓',
                  },
                },
                {
                  name: 'Auto Archive',
                  tooltip:
                    'Auto-archive marketing emails, cold pitches, and social updates to instantly declutter your inbox.',
                  availability: {
                    free: '',
                    pro: '',
                    business: '✓',
                    enterprise: '✓',
                  },
                },
                {
                  name: 'Auto Labels',
                  tooltip:
                    'Organize emails automatically with categories like marketing or social updates, or create your own Auto Labels for any criteria.',
                  availability: {
                    free: '',
                    pro: '',
                    business: '✓',
                    enterprise: '✓',
                  },
                },
                {
                  name: 'Auto Reminders',
                  tooltip: 'Get auto reminders to follow up on emails that need a response.',
                  availability: {
                    free: '',
                    pro: '',
                    business: '✓',
                    enterprise: '✓',
                  },
                },
                {
                  name: 'Auto Summarize',
                  tooltip:
                    'See updated conversation summaries above each thread as new emails arrive.',
                  availability: {
                    free: '',
                    pro: '',
                    business: '✓',
                    enterprise: '✓',
                  },
                },
                {
                  name: 'Autocorrect',
                  tooltip: 'Autocorrect typos, including punctuation and capitalization errors.',
                  availability: {
                    free: '',
                    pro: '',
                    business: '✓',
                    enterprise: '✓',
                  },
                },
                {
                  name: 'Autocomplete',
                  tooltip:
                    'Cut typing time by up to 50% with autocomplete suggestions for greetings, common words, and frequent phrases.',
                  availability: {
                    free: '',
                    pro: '',
                    business: '✓',
                    enterprise: '✓',
                  },
                },
                {
                  name: 'Custom Auto Labels',
                  tooltip:
                    'Create custom Auto Labels based on subject, sender, recipient, or label to organize similar emails.',
                  availability: {
                    free: '',
                    pro: '',
                    business: '✓',
                    enterprise: '✓',
                  },
                },
              ],
            },
            {
              name: 'Email productivity',
              features: [
                {
                  name: 'Split Inbox',
                  tooltip:
                    'Divide your inbox into priority areas such as Team, Google Docs, or GitHub.',
                  availability: {
                    free: '',
                    pro: '',
                    business: '✓',
                    enterprise: '✓',
                  },
                },
                {
                  name: 'Snippets',
                  tooltip:
                    'Automate frequent responses with custom phrases or full emails, including attachments and recipients.',
                  availability: {
                    free: '',
                    pro: '',
                    business: '✓',
                    enterprise: '✓',
                  },
                },
                {
                  name: 'Remind Me',
                  tooltip: 'Schedule follow-up reminders so you never miss a crucial message.',
                  availability: {
                    free: '',
                    pro: '',
                    business: '✓',
                    enterprise: '✓',
                  },
                },
                {
                  name: 'Snooze',
                  tooltip: 'Easily remove inbox clutter by snoozing emails until the right time.',
                  availability: {
                    free: '',
                    pro: '',
                    business: '✓',
                    enterprise: '✓',
                  },
                },
                {
                  name: 'Unsubscribe',
                  tooltip:
                    'Unsubscribe instantly from any sender and mark all related messages as done at once.',
                  availability: {
                    free: '',
                    pro: '',
                    business: '✓',
                    enterprise: '✓',
                  },
                },
                {
                  name: 'Read Statuses',
                  tooltip:
                    'Know when and how people read your emails to follow up with strategic timing and context.',
                  availability: {
                    free: '',
                    pro: '',
                    business: '✓',
                    enterprise: '✓',
                  },
                },
                {
                  name: 'Quick Quote',
                  tooltip:
                    'Reply to long emails faster by quoting parts of a message and including key context.',
                  availability: {
                    free: '',
                    pro: '',
                    business: '✓',
                    enterprise: '✓',
                  },
                },
                {
                  name: 'Offline Support',
                  tooltip: 'Reply, search, and mark emails as done, even without network access.',
                  availability: {
                    free: '',
                    pro: '',
                    business: '✓',
                    enterprise: '✓',
                  },
                },
                {
                  name: 'Recent Opens',
                  tooltip:
                    'View a real-time feed of who’s opened your emails so you can respond at just the right time.',
                  availability: {
                    free: '',
                    pro: '',
                    business: '✓',
                    enterprise: '✓',
                  },
                },
                {
                  name: 'HubSpot, Salesforce, and Pipedrive (read-only) integrations',
                  tooltip: 'Craft messages with full context from your CRM without leaving Mail.',
                  availability: {
                    free: '',
                    pro: '',
                    business: '✓',
                    enterprise: '✓',
                  },
                },
              ],
            },
            {
              name: 'Calendaring and scheduling',
              features: [
                {
                  name: 'Calendar',
                  tooltip:
                    'View your daily and weekly schedules and create events, all without leaving Mail.',
                  availability: {
                    free: '',
                    pro: '',
                    business: '✓',
                    enterprise: '✓',
                  },
                },
                {
                  name: 'Share Availability and Find Time',
                  tooltip:
                    'See team availability, offer your availability, and schedule meetings for everyone, all from your inbox.',
                  availability: {
                    free: '',
                    pro: '',
                    business: '✓',
                    enterprise: '✓',
                  },
                },
                {
                  name: 'Zoom, Google Meet, and Microsoft Teams integrations',
                  tooltip:
                    'Easily insert meeting links and automatically add links to recurring events.',
                  availability: {
                    free: '',
                    pro: '',
                    business: '✓',
                    enterprise: '✓',
                  },
                },
              ],
            },
            {
              name: 'Team collaboration',
              features: [
                {
                  name: 'Shared Conversations',
                  tooltip: 'Share a live, up-to-date view of any email with your team.',
                  availability: {
                    free: '',
                    pro: '',
                    business: '✓',
                    enterprise: '✓',
                  },
                },
                {
                  name: 'Team Comments',
                  tooltip: 'Let your team comment directly on any email.',
                  availability: {
                    free: '',
                    pro: '',
                    business: '✓',
                    enterprise: '✓',
                  },
                },
                {
                  name: 'Shared Drafts',
                  tooltip: 'Share your draft for team feedback before sending.',
                  availability: {
                    free: '',
                    pro: '',
                    business: '✓',
                    enterprise: '✓',
                  },
                },
                {
                  name: 'Team Read Statuses',
                  tooltip:
                    'See when teammates read your emails so you can communicate with full, real-time context.',
                  availability: {
                    free: '',
                    pro: '',
                    business: '✓',
                    enterprise: '✓',
                  },
                },
                {
                  name: 'Team Reply Indicators',
                  tooltip:
                    'Prevent duplicate or overlapping replies by seeing when teammates are responding or scheduling a reply.',
                  availability: {
                    free: '',
                    pro: '',
                    business: '✓',
                    enterprise: '✓',
                  },
                },
                {
                  name: 'Team Scheduling',
                  tooltip:
                    'View team schedules directly in Mail to find the best times for any meeting.',
                  availability: {
                    free: '',
                    pro: '',
                    business: '✓',
                    enterprise: '✓',
                  },
                },
              ],
            },
          ],
        },
      ],
      coda: [
        {
          productName: 'Coda',
          sections: [
            {
              name: 'Features',
              features: [
                {
                  name: 'Coda Doc',
                  tooltip:
                    'Use docs with building blocks like pages and tables to enhance your writeups, trackers, and more.',
                  availability: {
                    free: 'Unlimited for unshared docs; 50 objects and 1,000 rows per doc',
                    pro: 'Unlimited',
                    business: 'Unlimited',
                    enterprise: 'Unlimited',
                  },
                },
                {
                  name: 'Automations',
                  tooltip: 'Autopilot important yet repetitive tasks like sending daily updates.',
                  availability: {
                    free: 'Low quota',
                    pro: 'Medium quota',
                    business: 'Unlimited',
                    enterprise: 'Unlimited',
                  },
                },
                {
                  name: 'Attachments',
                  tooltip: 'Add files to your doc easily.',
                  availability: {
                    free: '1 GB per doc',
                    pro: '5 GB per doc',
                    business: 'Unlimited',
                    enterprise: 'Unlimited',
                  },
                },
                {
                  name: 'Packs',
                  tooltip: 'Connect docs to your everyday apps and sync data using Packs.',
                  availability: {
                    free: 'Manual data refresh',
                    pro: 'Daily data refresh',
                    business: 'Hourly data refresh',
                    enterprise: 'Hourly data refresh',
                  },
                },
                {
                  name: 'Forms',
                  tooltip: 'Publish forms to collect responses from anyone.',
                  availability: {
                    free: 'Unlimited forms',
                    pro: 'Custom branding',
                    business: 'Custom branding',
                    enterprise: 'Custom branding',
                  },
                },
                {
                  name: 'Cross-doc',
                  tooltip: 'Sync tables across docs to establish a single source of truth.',
                  availability: {
                    free: 'Limited cross-doc tables',
                    pro: 'Cross-doc tables',
                    business: 'Cross-doc tables & actions',
                    enterprise: 'Cross-doc tables & actions',
                  },
                },
                {
                  name: 'Version history',
                  tooltip: 'Recover the info you need in seconds.',
                  availability: {
                    free: '7 days version history',
                    pro: '30 days version history',
                    business: 'Unlimited version history',
                    enterprise: 'Unlimited version history',
                  },
                },
              ],
            },
            {
              name: 'Collaboration and sharing',
              features: [
                {
                  name: 'Real-time collaboration',
                  tooltip: 'See real-time changes and active editors in your doc.',
                  availability: {
                    free: '✓',
                    pro: '✓',
                    business: '✓',
                    enterprise: '✓',
                  },
                },
                {
                  name: 'Doc sharing',
                  tooltip:
                    'Share docs directly by link or through folders, with full access control.',
                  availability: {
                    free: '✓',
                    pro: '✓',
                    business: '✓',
                    enterprise: '✓',
                  },
                },
                {
                  name: 'Sync pages',
                  tooltip: 'Multi-home the content your team uses most often in docs.',
                  availability: {
                    free: '✓',
                    pro: '✓',
                    business: '✓',
                    enterprise: '✓',
                  },
                },
                {
                  name: 'Hidden pages',
                  tooltip: 'Simplify docs by hiding unused pages and archiving outdated content.',
                  availability: {
                    free: '',
                    pro: '✓',
                    business: '✓',
                    enterprise: '✓',
                  },
                },
                {
                  name: 'Custom domains',
                  tooltip: 'Publish docs to your branded domain.',
                  availability: {
                    free: '',
                    pro: '✓',
                    business: '✓',
                    enterprise: '✓',
                  },
                },
                {
                  name: 'Advanced locking',
                  tooltip:
                    'Restrict permissions for unlocking, adding pages, and configuring doc settings.',
                  availability: {
                    free: '',
                    pro: '',
                    business: '✓',
                    enterprise: '✓',
                  },
                },
                {
                  name: 'Enforced dependencies',
                  tooltip: 'Automatically shift the dates of items as their dependencies change.',
                  availability: {
                    free: '',
                    pro: '',
                    business: '✓',
                    enterprise: '✓',
                  },
                },
              ],
            },
            {
              name: 'Workspace features',
              features: [
                {
                  name: 'Unlimited folders',
                  tooltip: 'Organize docs in separate workspace folders.',
                  availability: {
                    free: '✓',
                    pro: '✓',
                    business: '✓',
                    enterprise: '✓',
                  },
                },
                {
                  name: 'Custom icons',
                  tooltip: 'Personalize docs and domains with unlimited custom icons.',
                  availability: {
                    free: '',
                    pro: '✓',
                    business: '✓',
                    enterprise: '✓',
                  },
                },
                {
                  name: 'Manage folder access',
                  tooltip:
                    'Set permissions for who can view, comment, edit, and manage folders and docs.',
                  availability: {
                    free: '',
                    pro: '',
                    business: '✓',
                    enterprise: '✓',
                  },
                },
                {
                  name: 'Doc and Page analytics',
                  tooltip: 'Track doc engagement at a glance.',
                  availability: {
                    free: '',
                    pro: '',
                    business: '',
                    enterprise: '✓',
                  },
                },
                {
                  name: 'Advanced user management',
                  tooltip:
                    'Manage user roles and build reports easily with the Admin pack and API.',
                  availability: {
                    free: '',
                    pro: '',
                    business: '',
                    enterprise: '✓',
                  },
                },
                {
                  name: 'Domain capture',
                  tooltip: 'Control users and data across your organization’s domain securely.',
                  availability: {
                    free: '',
                    pro: '',
                    business: '',
                    enterprise: '✓',
                  },
                },
              ],
            },
          ],
        },
      ],
      shared: [
        {
          productName: 'Across the suite',
          sections: [
            {
              name: 'Support and success',
              features: [
                {
                  name: 'Help Center',
                  tooltip: 'Get reliable help from thorough knowledge bases for every product.',
                  availability: {
                    free: '✓',
                    pro: '✓',
                    business: '✓',
                    enterprise: '✓',
                  },
                },
                {
                  name: 'Webinar and trainings',
                  tooltip: 'Improve your skills with expert-led webinars and training.',
                  availability: {
                    free: '✓',
                    pro: '✓',
                    business: '✓',
                    enterprise: 'Private',
                  },
                },
                {
                  name: 'Customer success',
                  tooltip:
                    'Rely on a team of experts dedicated to helping you get the most from your suite.',
                  availability: {
                    free: '',
                    pro: '',
                    business: '',
                    enterprise: 'Assigned',
                  },
                },
                {
                  name: 'Success planning',
                  tooltip:
                    'Build a successful plan informed by your business goals with expert support from our team.',
                  availability: {
                    free: '',
                    pro: '',
                    business: '',
                    enterprise: '✓',
                  },
                },
                {
                  name: 'Value reviews',
                  tooltip: 'Collaborate with our team to maximize your suite’s value.',
                  availability: {
                    free: '',
                    pro: '',
                    business: '',
                    enterprise: '✓',
                  },
                },
                {
                  name: 'Guided onboarding',
                  tooltip: 'Ensure a smooth rollout with a dedicated implementation team.',
                  availability: {
                    free: '',
                    pro: '',
                    business: '',
                    enterprise: '✓',
                  },
                },
              ],
            },
            {
              name: 'Security, compliance, and administration',
              features: [
                {
                  name: 'SAML SSO',
                  tooltip: 'Authenticate logins securely with your single sign-on provider.',
                  availability: {
                    free: '',
                    pro: '',
                    business: '',
                    enterprise: '✓',
                  },
                },
                {
                  name: 'SCIM',
                  tooltip: 'Automate the provisioning process.',
                  availability: {
                    free: '',
                    pro: '',
                    business: '',
                    enterprise: '✓',
                  },
                },
                {
                  name: 'SOC 2 Type 2',
                  tooltip:
                    'Know your data is protected: Our SOC 2 Type 2 reports show our commitment to the most rigorous security, confidentiality, integrity, and privacy standards in the industry.',
                  availability: {
                    free: '',
                    pro: '',
                    business: '',
                    enterprise: '✓',
                  },
                },
                {
                  name: 'Audit logs',
                  tooltip: 'Ensure continuous compliance with transactional event monitoring.',
                  availability: {
                    free: '',
                    pro: '',
                    business: '',
                    enterprise: '✓',
                  },
                },
                {
                  name: 'Organization-level controls',
                  tooltip: 'Configure usage by groups.',
                  availability: {
                    free: '',
                    pro: '',
                    business: '',
                    enterprise: '✓',
                  },
                },
                {
                  name: 'HIPAA',
                  tooltip: 'Protect sensitive health data with HIPAA-compliant safeguards.',
                  availability: {
                    free: '',
                    pro: '',
                    business: '',
                    enterprise: 'Coda only',
                  },
                },
                {
                  name: 'Legal hold and e-discovery',
                  tooltip:
                    'Preserve information for litigation with our efficient and secure native legal hold solution.',
                  availability: {
                    free: '',
                    pro: '',
                    business: '',
                    enterprise: 'Coda only',
                  },
                },
                {
                  name: 'BYOK',
                  tooltip:
                    'Exercise control over encryption key access, rotation, and revocation, and view access logs.',
                  availability: {
                    free: '',
                    pro: '',
                    business: '',
                    enterprise: 'Grammarly only',
                  },
                },
              ],
            },
          ],
        },
      ],
    },
  },
}
